/**
 * v4.34.15 — Centralized timestamp parsing & display.
 *
 * Root cause of "4-hour offset" bug: some backend endpoints stored
 * datetimes as raw BSON Date values (not `.isoformat()` strings).
 * PyMongo returns those naive (no tzinfo), which serialize to JSON
 * without a TZ suffix (e.g. `"2026-06-02T16:25:00"`). Browsers (per
 * older specs / Safari behavior) then interpret that as local time
 * instead of UTC, causing displayed times to drift by the local
 * offset.
 *
 * This module fixes the bug *defensively at the display layer*:
 *
 *   1. `parseUtc(input)` — accepts any ISO string. If the string
 *      has no timezone marker (`Z` / `+hh:mm` / `-hh:mm`), append
 *      `Z` before parsing so the value is anchored to UTC.
 *
 *   2. Formatters (`formatDateTime`, `formatDate`, `formatTime`,
 *      `formatRelative`) always use the browser's local timezone
 *      via `Intl.DateTimeFormat`. They never hard-code UTC.
 *
 * No backend changes. No schema changes. No timezone settings.
 * Pure display-layer normalization.
 */
import { formatDistanceToNow } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale/fr';
import { es as esLocale } from 'date-fns/locale/es';
import { enUS as enLocale } from 'date-fns/locale/en-US';

const TZ_SUFFIX_REGEX = /(Z|[+-]\d{2}:?\d{2})$/;
const INVALID = new Date('invalid');

/**
 * Parse a timestamp into a `Date` anchored to UTC.
 *
 * - ISO strings with explicit TZ → parsed as-is.
 * - ISO strings WITHOUT TZ       → treated as UTC (append `Z`).
 * - Already-Date inputs          → returned unchanged.
 * - Falsy / unparseable inputs   → returns an Invalid Date (so
 *   chained `.toLocaleString()` / `.getTime()` calls match the
 *   behavior of the previously-used `new Date(<bad>)` and don't
 *   throw — preserves caller-side behavior across the refactor).
 */
export function parseUtc(input) {
    if (input == null || input === '') return INVALID;
    if (input instanceof Date) return isNaN(input.getTime()) ? INVALID : input;
    if (typeof input === 'number') {
        const d = new Date(input);
        return isNaN(d.getTime()) ? INVALID : d;
    }
    if (typeof input !== 'string') return INVALID;
    const s = input.trim();
    if (!s) return INVALID;
    // Date-only strings (`YYYY-MM-DD`) are already UTC per ES spec — leave alone.
    const normalized = /T/.test(s) && !TZ_SUFFIX_REGEX.test(s) ? `${s}Z` : s;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? INVALID : d;
}

function resolveLocale(lang) {
    switch ((lang || '').toLowerCase().slice(0, 2)) {
        case 'fr': return frLocale;
        case 'es': return esLocale;
        default:   return enLocale;
    }
}

function bcp47(lang) {
    switch ((lang || '').toLowerCase().slice(0, 2)) {
        case 'fr': return 'fr-CA';
        case 'es': return 'es';
        default:   return 'en-US';
    }
}

function isValid(d) {
    return d instanceof Date && !isNaN(d.getTime());
}

/**
 * Full date + time (e.g. "Jun 2, 2026, 12:25 PM").
 * Uses browser's local timezone.
 */
export function formatDateTime(input, { lang, options } = {}) {
    const d = parseUtc(input);
    if (!isValid(d)) return '';
    return d.toLocaleString(bcp47(lang), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...(options || {}),
    });
}

/**
 * Date only (e.g. "Jun 2, 2026"). Browser-local.
 */
export function formatDate(input, { lang, options } = {}) {
    const d = parseUtc(input);
    if (!isValid(d)) return '';
    return d.toLocaleDateString(bcp47(lang), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(options || {}),
    });
}

/**
 * Time only (e.g. "12:25 PM"). Browser-local.
 */
export function formatTime(input, { lang, options } = {}) {
    const d = parseUtc(input);
    if (!isValid(d)) return '';
    return d.toLocaleTimeString(bcp47(lang), {
        hour: 'numeric',
        minute: '2-digit',
        ...(options || {}),
    });
}

/**
 * Compact date + time (e.g. "Jun 2, 12:25 PM").
 * Common form used across dashboards and feeds.
 */
export function formatShort(input, { lang } = {}) {
    return formatDateTime(input, {
        lang,
        options: { year: undefined, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    });
}

/**
 * Relative time (e.g. "2 min ago", "3 hours ago"). Browser-local clock.
 */
export function formatRelative(input, { lang, addSuffix = true } = {}) {
    const d = parseUtc(input);
    if (!isValid(d)) return '';
    try {
        return formatDistanceToNow(d, { addSuffix, locale: resolveLocale(lang) });
    } catch {
        return '';
    }
}

/**
 * ISO 8601 string fit for backend submission (always UTC).
 * Used when generating a *new* current-time payload from the frontend.
 */
export function nowUtcIso() {
    return new Date().toISOString();
}

/**
 * Build the absolute tooltip companion to a relative label.
 * Returns something like "Jun 2, 2026, 12:25 PM EDT".
 */
export function formatAbsoluteTooltip(input, { lang } = {}) {
    const d = parseUtc(input);
    if (!isValid(d)) return '';
    return d.toLocaleString(bcp47(lang), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    });
}
